/**
 * Passkey (WebAuthn) Utilities
 * 
 * Provides device-level identification using FIDO2/WebAuthn passkeys.
 * Passkeys are stored at OS level and persist across private browsing.
 * 
 * Key Features:
 * - Mandatory biometric/PIN authentication
 * - Session-based caching (one prompt per browser tab)
 * - Automatic re-registration after database reset
 * - Graceful fallback for unsupported devices
 */

import { detectInAppBrowser } from './browserDetection';
import { reportClientEvent } from './clientTelemetry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Helper to emit debug logs as custom events
function emitDebugLog(message, type = 'info') {
  const event = new CustomEvent('passkeyDebugLog', { 
    detail: { message, type, timestamp: new Date().toLocaleTimeString() }
  });
  window.dispatchEvent(event);
  console.log(`[${type.toUpperCase()}] ${message}`);
}

// Storage keys (now using localStorage instead of sessionStorage for better UX)
// localStorage persists across tabs/windows and survives browser restarts
// Still works in private browsing (cleared when private session ends)
// v2: Changed key names to force re-authentication after fallback logic update
const STORAGE_KEYS = {
  DEVICE_FINGERPRINT: 'karaoke_device_fingerprint_v2',
  USER_ID: 'karaoke_user_id_v2',
  AUTHENTICATED: 'karaoke_authenticated_v2',
  LAST_USED_NAME: 'karaoke_last_used_name_v2',
  HAS_PASSKEY: 'karaoke_has_passkey_v2', // tracks if device has registered passkey
  USING_FALLBACK: 'karaoke_using_fallback_v2', // tracks if device is using fallback mode
  // Fix #6 (shipped 2026-08-25): removed AUTH_FAIL_COUNT — the counter was never
  // read to gate any behavior. Historical artifact from an earlier
  // auto-fallback design. Legacy `karaoke_auth_fail_count_v2` /
  // `karaoke_auth_fail_count` keys are still purged by `clearAllAuthData`
  // below so any lingering values on returning users' devices are
  // cleaned up on any auth reset.
};

/**
 * Clear all authentication data (for admin reset)
 */
export function clearAllAuthData() {
  // Clear v2 keys
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });

  // Also clear old v1 keys AND the retired v2 AUTH_FAIL_COUNT
  // (Fix #6 2026-09-24) so any lingering values on returning users'
  // devices get cleaned up on any auth reset.
  const oldKeys = [
    'karaoke_device_fingerprint',
    'karaoke_user_id',
    'karaoke_authenticated',
    'karaoke_last_used_name',
    'karaoke_has_passkey',
    'karaoke_using_fallback',
    'karaoke_auth_fail_count',
    'karaoke_auth_fail_count_v2'
  ];
  oldKeys.forEach(key => {
    localStorage.removeItem(key);
  });

  return true;
}

// Fix #6 (shipped 2026-08-25): removed getAuthFailCount / incrementAuthFailCount /
// resetAuthFailCount — the counter was never read to gate any behavior.
// Legacy call sites cleaned up in this same commit.

/**
 * Manually enable fallback mode (user chose to switch to classic mode)
 *
 * Part B of the 2026-09-25 fallback-persistence fix. Fire-and-forget POST
 * to `/auth/fallback/register` so the DB has a user doc for the freshly
 * minted fingerprint *before* the next refresh's validate-fallback call.
 * Prior to this, the DB row was created lazily on first `/queue/join`,
 * meaning any refresh in the "entered password but haven't joined a
 * song yet" window failed validation and reset the user.
 *
 * localStorage is written synchronously so the caller's UI can flip
 * to fallback state immediately — we don't await the network call.
 * If the register POST fails silently, the join-time insert still
 * covers the eventual case; the register call is a proactive
 * optimization, not a correctness dependency.
 */
export function enableFallbackMode() {
  const fallbackFingerprint = generateFallbackFingerprint();
  const fallbackUserId = 'fallback_' + Date.now();

  localStorage.setItem(STORAGE_KEYS.DEVICE_FINGERPRINT, fallbackFingerprint);
  localStorage.setItem(STORAGE_KEYS.USER_ID, fallbackUserId);
  localStorage.setItem(STORAGE_KEYS.AUTHENTICATED, 'true');
  localStorage.setItem(STORAGE_KEYS.USING_FALLBACK, 'true');

  // Fire-and-forget: register the fallback user server-side so refresh-
  // time validation immediately finds them. Any failure here is a
  // silent no-op — the join path still creates the doc if this call
  // never completes.
  try {
    fetch(`${API}/auth/fallback/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        device_fingerprint: fallbackFingerprint,
        user_id: fallbackUserId,
      }),
    }).then((res) => {
      if (!res.ok) {
        emitDebugLog(
          `⚠️ [fallback_register_non_ok] Proactive fallback register returned HTTP ${res.status} - join-time insert will cover`,
          'warning'
        );
      }
    }).catch((err) => {
      const errName = (err && err.name) || 'UnknownError';
      emitDebugLog(
        `⚠️ [fallback_register_network_error] Proactive fallback register threw (${errName}) - join-time insert will cover`,
        'warning'
      );
    });
  } catch (_e) {
    // Defense-in-depth — should never fire, but ensures localStorage
    // state is still written even if `fetch` is somehow unavailable.
  }

  return {
    success: true,
    deviceFingerprint: fallbackFingerprint,
    userId: fallbackUserId,
    usingFallback: true
  };
}

/**
 * Check if WebAuthn is supported in this browser
 */
export function isPasskeySupported() {
  return window.PublicKeyCredential !== undefined && 
         navigator.credentials !== undefined;
}

/**
 * Check if platform authenticator (Face ID, Touch ID, Windows Hello) is available
 */
export async function isPlatformAuthenticatorAvailable() {
  if (!isPasskeySupported()) {
    return false;
  }
  
  try {
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch (error) {
    console.error('Error checking platform authenticator:', error);
    return false;
  }
}

/**
 * Get platform-specific authenticator name
 */
export function getAuthenticatorName() {
  const platform = navigator.platform.toLowerCase();
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (platform.includes('mac') || platform.includes('iphone') || platform.includes('ipad')) {
    return 'Face ID or Touch ID';
  } else if (platform.includes('win')) {
    return 'Windows Hello';
  } else if (userAgent.includes('android')) {
    return 'Fingerprint or PIN';
  } else {
    return 'Biometric or PIN';
  }
}

/**
 * Convert base64url string to ArrayBuffer
 */
function base64urlToArrayBuffer(base64url) {
  // Add padding if needed
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const base64Padded = base64 + padding;
  
  const binary = atob(base64Padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Helper function to handle WebAuthn calls with auto-retry on "document not focused" error.
 * WebAuthn requires the document to be focused - this can fail transiently when:
 * - A notification briefly steals focus
 * - System UI (Control Center, etc.) appears
 * - Page loads while user is switching apps
 * 
 * This wrapper retries once after a short delay for these transient focus issues.
 */
async function webAuthnWithRetry(operation, operationName) {
  try {
    return await operation();
  } catch (error) {
    // Check if this is a "document not focused" error
    const isNotFocusedError = error.name === 'NotAllowedError' && 
      (error.message?.toLowerCase().includes('not focused') || 
       error.message?.toLowerCase().includes('not visible'));
    
    if (isNotFocusedError) {
      emitDebugLog(`⚠️ ${operationName} failed: Document not focused - retrying in 500ms...`, 'warning');
      
      // Wait briefly for focus to return
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Retry once
      try {
        emitDebugLog(`🔄 Retrying ${operationName}...`, 'info');
        return await operation();
      } catch (retryError) {
        emitDebugLog(`❌ ${operationName} retry also failed: ${retryError.message}`, 'error');
        throw retryError;
      }
    }
    
    // Not a focus error, throw original
    throw error;
  }
}

/**
 * Convert ArrayBuffer to base64url string
 */
function arrayBufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Generate a random device fingerprint (fallback for unsupported devices)
 */
function generateFallbackFingerprint() {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);
  return 'fallback_' + arrayBufferToBase64url(randomBytes.buffer);
}

/**
 * Register a new passkey
 * SIMPLIFIED: No deviceFingerprint parameter needed - backend uses credential_id
 */
export async function registerPasskey(userId, userName, existingCredentialIds = []) {
  try {
    emitDebugLog('🔐 Starting passkey registration...', 'info');
    emitDebugLog(`API URL: ${API}`, 'info');
    emitDebugLog(`User ID: ${userId}`, 'info');
    emitDebugLog(`Excluding ${existingCredentialIds.length} existing credentials`, 'info');
    
    // Check if platform authenticator is actually available
    let platformAvailable = false;
    try {
      platformAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      emitDebugLog(`Platform authenticator available: ${platformAvailable}`, 'info');
    } catch (e) {
      emitDebugLog(`Could not check platform authenticator availability: ${e.message}`, 'warning');
    }
    
    // Start registration (with existing credentials to exclude)
    let startResponse;
    try {
      startResponse = await fetch(`${API}/auth/passkey/register/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          user_name: userName,
          existing_credential_ids: existingCredentialIds  // Pass credentials to exclude
        })
      });
    } catch (networkError) {
      emitDebugLog(`Network error: ${networkError.message}`, 'error');
      throw new Error(`Network error: Cannot reach backend at ${API}. ${networkError.message}`);
    }
    
    if (!startResponse.ok) {
      const errorText = await startResponse.text();
      emitDebugLog(`Registration start failed: ${startResponse.status} - ${errorText}`, 'error');
      throw new Error(`Failed to start registration: ${startResponse.status}`);
    }
    
    const responseData = await startResponse.json();
    emitDebugLog(`Registration options received from backend`, 'success');
    const { options, challenge_key } = responseData;
    
    // Convert base64url strings to ArrayBuffers
    options.challenge = base64urlToArrayBuffer(options.challenge);
    options.user.id = base64urlToArrayBuffer(options.user.id);
    
    if (options.excludeCredentials) {
      options.excludeCredentials = options.excludeCredentials.map(cred => ({
        ...cred,
        id: base64urlToArrayBuffer(cred.id)
      }));
    }
    
    emitDebugLog('✨ Prompting for biometric registration...', 'info');
    emitDebugLog(`RP ID: ${options.rp.id}`, 'info');
    emitDebugLog(`Timeout: ${options.timeout}ms (${options.timeout/1000}s)`, 'info');
    emitDebugLog(`User Verification: ${options.authenticatorSelection?.userVerification}`, 'info');
    emitDebugLog(`Resident Key: ${options.authenticatorSelection?.residentKey}`, 'info');
    emitDebugLog(`Attestation: ${options.attestation}`, 'info');
    emitDebugLog(`Authenticator Attachment (backend): ${options.authenticatorSelection?.authenticatorAttachment || 'not specified'}`, 'info');
    
    // If backend set PLATFORM but it's not actually available, remove it to avoid NotAllowedError
    if (options.authenticatorSelection?.authenticatorAttachment === 'platform' && !platformAvailable) {
      emitDebugLog('⚠️ Backend requested platform but not available - removing restriction', 'warning');
      delete options.authenticatorSelection.authenticatorAttachment;
    }
    
    emitDebugLog(`Authenticator Attachment (final): ${options.authenticatorSelection?.authenticatorAttachment || 'not specified'}`, 'info');
    emitDebugLog('About to call navigator.credentials.create()...', 'info');
    
    // Create credential (this shows the biometric prompt!)
    // Use retry wrapper to handle transient "document not focused" errors
    const credential = await webAuthnWithRetry(
      () => navigator.credentials.create({ publicKey: options }),
      'Registration'
    );
    
    if (!credential) {
      emitDebugLog('Registration returned null credential', 'error');
      throw new Error('Registration cancelled or failed');
    }
    
    emitDebugLog('✅ Biometric registered!', 'success');
    
    // Prepare credential data for server
    const credentialData = {
      id: credential.id,
      rawId: arrayBufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
        attestationObject: arrayBufferToBase64url(credential.response.attestationObject),
        transports: credential.response.getTransports ? credential.response.getTransports() : []
      }
    };
    
    // Complete registration
    // Fix #8 (shipped 2026-08-27): send cached device_fingerprint so the
    // backend can bridge identity for a returning user whose
    // passkey was lost (see server.py register/finish docstring).
    // Empty string sent when localStorage has no cache — backend
    // treats that as "no bridge available, create fresh user".
    const cachedDeviceFingerprint = localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT) || '';
    const finishResponse = await fetch(`${API}/auth/passkey/register/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        challenge_key: challenge_key,
        credential: credentialData,
        device_fingerprint: cachedDeviceFingerprint,  // Fix #8 bridge anchor
      })
    });
    
    if (!finishResponse.ok) {
      const errorText = await finishResponse.text();
      emitDebugLog(`Failed to complete registration: ${errorText}`, 'error');
      throw new Error('Failed to complete registration');
    }
    
    const result = await finishResponse.json();
    
    // Fix #8 (shipped 2026-08-27): use the backend's resolved user_id (which
    // is either the same as our client-generated one, OR the ORIGINAL
    // user_id if a bridge lookup matched an existing user). This
    // keeps the client's USER_ID cache in sync with the server's
    // identity after a passkey-loss recovery.
    const resolvedUserId = result.user_id || userId;

    // Store in localStorage (persists across tabs and browser restarts)
    localStorage.setItem(STORAGE_KEYS.DEVICE_FINGERPRINT, result.device_fingerprint);
    localStorage.setItem(STORAGE_KEYS.USER_ID, resolvedUserId);
    localStorage.setItem(STORAGE_KEYS.AUTHENTICATED, 'true');
    localStorage.setItem(STORAGE_KEYS.HAS_PASSKEY, 'true'); // Mark that device has passkey
    
    // CRITICAL: Verify localStorage write succeeded before proceeding
    const verifyFingerprint = localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT);
    if (verifyFingerprint !== result.device_fingerprint) {
      emitDebugLog('❌ CRITICAL: localStorage write failed - fingerprint mismatch!', 'error');
      emitDebugLog(`Expected: ${result.device_fingerprint}`, 'error');
      emitDebugLog(`Got: ${verifyFingerprint}`, 'error');
      throw new Error('Failed to cache registration data - please try again');
    }
    
    emitDebugLog('🎉 Passkey registration complete!', 'success');
    emitDebugLog(`✅ Fingerprint verified: ${result.device_fingerprint}`, 'success');
    
    return {
      success: true,
      deviceFingerprint: result.device_fingerprint,
      userId: resolvedUserId
    };
    
  } catch (error) {
    emitDebugLog(`❌ Registration failed: ${error.message}`, 'error');
    emitDebugLog(`Error type: ${error.name}`, 'error');
    if (error.code) {
      emitDebugLog(`Error code: ${error.code}`, 'error');
    }
    
    // Provide more specific error messages for common Android issues
    //
    // 2026-10-07 (Mike incident follow-up): Preserve the ORIGINAL
    // error name/message as properties on the wrapped Error so callers
    // can log the real WebAuthn error type in telemetry. Without this,
    // every registration failure was logged as `error_name=Error`
    // because the `new Error('Registration not allowed...')` wrap
    // zeroed out the original `.name`. We attach `originalErrorName`
    // and `originalErrorMessage` so App.js's `passkey_auth_failed`
    // emitter can pass them through — gives us visibility into whether
    // the failure was NotAllowedError (biometric/timeout/cancel) vs
    // SecurityError (origin mismatch) vs TimeoutError (ceremony cap)
    // vs something else. Purely additive — doesn't change throw
    // behavior or any existing catch handlers.
    const _attachOrig = (wrapped) => {
      try {
        wrapped.originalErrorName = error && error.name ? error.name : null;
        wrapped.originalErrorMessage = error && error.message ? String(error.message).slice(0, 300) : null;
      } catch (_e) { /* attribute attach best-effort */ }
      return wrapped;
    };
    if (error.name === 'NotAllowedError') {
      throw _attachOrig(new Error('Registration not allowed. This may be due to: (1) User cancelled, (2) Timeout, or (3) Too many failed attempts. Please try again.'));
    } else if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
      throw _attachOrig(new Error('Registration timed out. Please try again and complete the biometric prompt more quickly.'));
    } else if (error.name === 'NotSupportedError') {
      throw _attachOrig(new Error('Passkeys are not supported on this device/browser. Please use a different device or update your browser.'));
    } else if (error.name === 'InvalidStateError') {
      // Don't wrap InvalidStateError - let it bubble up so we can catch it properly
      throw error;
    }
    
    throw error;
  }
}

/**
 * Authenticate using discoverable credentials (no specific credential IDs)
 * This shows Face ID directly without the "Sign In" modal
 */
async function authenticateWithDiscoverableCredentials() {
  try {
    emitDebugLog('🔐 Starting discoverable credentials authentication...', 'info');
    
    const startResponse = await fetch(`${API}/auth/passkey/login/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        use_discoverable: true
      })
    });
    
    if (!startResponse.ok) {
      throw new Error('Failed to start authentication');
    }
    
    const startData = await startResponse.json();
    
    // Handle no passkeys case
    if (startData.status === 'no_passkeys') {
      throw new Error('No passkeys in database');
    }
    
    // Get authentication options
    const options = startData.options;
    options.challenge = base64urlToArrayBuffer(options.challenge);
    
    // For discoverable credentials, use empty allowCredentials
    if (options.allowCredentials && options.allowCredentials.length > 0) {
      options.allowCredentials = options.allowCredentials.map(cred => ({
        ...cred,
        id: base64urlToArrayBuffer(cred.id)
      }));
    }
    
    emitDebugLog('✨ Prompting for biometric authentication...', 'info');
    // Use retry wrapper to handle transient "document not focused" errors
    const assertion = await webAuthnWithRetry(
      () => navigator.credentials.get({ publicKey: options }),
      'Authentication'
    );
    
    if (!assertion) {
      throw new Error('Authentication cancelled or failed');
    }
    
    emitDebugLog('✅ Biometric authenticated!', 'success');
    
    // Complete authentication
    const credentialData = {
      id: assertion.id,
      rawId: arrayBufferToBase64url(assertion.rawId),
      type: assertion.type,
      response: {
        clientDataJSON: arrayBufferToBase64url(assertion.response.clientDataJSON),
        authenticatorData: arrayBufferToBase64url(assertion.response.authenticatorData),
        signature: arrayBufferToBase64url(assertion.response.signature),
        userHandle: assertion.response.userHandle ? arrayBufferToBase64url(assertion.response.userHandle) : null
      }
    };
    
    const finishResponse = await fetch(`${API}/auth/passkey/login/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        challenge_key: startData.challenge_key,
        credential: credentialData
      })
    });
    
    if (!finishResponse.ok) {
      throw new Error('Failed to complete authentication');
    }
    
    const result = await finishResponse.json();
    
    // Store in localStorage (persists across tabs and browser restarts)
    localStorage.setItem(STORAGE_KEYS.DEVICE_FINGERPRINT, result.device_fingerprint);
    localStorage.setItem(STORAGE_KEYS.USER_ID, result.user_id);
    localStorage.setItem(STORAGE_KEYS.AUTHENTICATED, 'true');
    localStorage.setItem(STORAGE_KEYS.HAS_PASSKEY, 'true'); // Mark that device has passkey
    
    if (result.last_used_name) {
      localStorage.setItem(STORAGE_KEYS.LAST_USED_NAME, result.last_used_name);
    }
    
    // CRITICAL: Verify localStorage write succeeded before proceeding
    const verifyFingerprint = localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT);
    if (verifyFingerprint !== result.device_fingerprint) {
      emitDebugLog('❌ CRITICAL: localStorage write failed - fingerprint mismatch!', 'error');
      emitDebugLog(`Expected: ${result.device_fingerprint}`, 'error');
      emitDebugLog(`Got: ${verifyFingerprint}`, 'error');
      throw new Error('Failed to cache authentication data - please try again');
    }
    
    emitDebugLog('🎉 Authentication complete!', 'success');
    emitDebugLog(`✅ Fingerprint verified: ${result.device_fingerprint}`, 'success');
    
    return {
      success: true,
      deviceFingerprint: result.device_fingerprint,
      userId: result.user_id,
      lastUsedName: result.last_used_name
    };
    
  } catch (error) {
    emitDebugLog(`❌ Discoverable authentication failed: ${error.message}`, 'error');
    throw error;
  }
}

/**
 * Authenticate with an existing passkey
 */
export async function authenticateWithPasskey() {
  // Capture start time for the duration-gated suppression check in the
  // NotAllowedError catch below (shipped 2026-10-07 as Fix #4 Android
  // replacement). `performance.now()` is a monotonic clock not affected
  // by system clock changes.
  const _authT0 = (typeof performance !== 'undefined' && performance.now)
    ? performance.now()
    : Date.now();
  try {
    emitDebugLog('🔐 Starting passkey authentication...', 'info');
    emitDebugLog(`API URL: ${API}`, 'info');
    
    // Start authentication
    let startResponse;
    try {
      startResponse = await fetch(`${API}/auth/passkey/login/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
    } catch (networkError) {
      emitDebugLog(`Network error: ${networkError.message}`, 'error');
      throw new Error(`Network error: Cannot reach backend at ${API}. ${networkError.message}`);
    }
    
    if (!startResponse.ok) {
      const errorText = await startResponse.text();
      emitDebugLog(`Authentication start failed: ${startResponse.status} - ${errorText}`, 'error');
      throw new Error(`Failed to start authentication: ${startResponse.status}`);
    }
    
    const startData = await startResponse.json();
    emitDebugLog(`Backend response: ${JSON.stringify(startData)}`, 'info');
    
    // Check if registration is needed
    if (startData.status === 'no_passkeys') {
      // Database has no passkeys - skip authentication and go straight to registration
      // This avoids the confusing "Sign In" modal that would appear if we tried to authenticate
      emitDebugLog('📝 No passkeys in database - skipping auth, going straight to registration', 'warning');
      
      // Return immediately to trigger registration without showing authentication prompt
      return {
        status: 'registration_needed',
        message: 'No passkeys in database'
      };
    }
    
    const { options, challenge_key } = startData;
    
    // Convert base64url strings to ArrayBuffers
    options.challenge = base64urlToArrayBuffer(options.challenge);
    
    if (options.allowCredentials) {
      options.allowCredentials = options.allowCredentials.map(cred => ({
        ...cred,
        id: base64urlToArrayBuffer(cred.id)
      }));
    }
    
    emitDebugLog('✨ Prompting for biometric authentication...', 'info');
    emitDebugLog(`Credentials count: ${options.allowCredentials?.length || 0}`, 'info');
    
    // Get credential (this shows the biometric prompt!)
    // Use retry wrapper to handle transient "document not focused" errors
    const assertion = await webAuthnWithRetry(
      () => navigator.credentials.get({ publicKey: options }),
      'Authentication'
    );
    
    if (!assertion) {
      emitDebugLog('Authentication returned null assertion', 'error');
      throw new Error('Authentication cancelled or failed');
    }
    
    emitDebugLog('✅ Biometric authenticated!', 'success');
    
    // Prepare assertion data
    const assertionData = {
      id: assertion.id,
      rawId: arrayBufferToBase64url(assertion.rawId),
      type: assertion.type,
      response: {
        clientDataJSON: arrayBufferToBase64url(assertion.response.clientDataJSON),
        authenticatorData: arrayBufferToBase64url(assertion.response.authenticatorData),
        signature: arrayBufferToBase64url(assertion.response.signature),
        userHandle: assertion.response.userHandle 
          ? arrayBufferToBase64url(assertion.response.userHandle) 
          : null
      }
    };
    
    // Complete authentication
    const finishResponse = await fetch(`${API}/auth/passkey/login/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        challenge_key: challenge_key,
        credential: assertionData
      })
    });
    
    if (!finishResponse.ok) {
      throw new Error('Failed to complete authentication');
    }
    
    const result = await finishResponse.json();
    
    // Check if re-registration is needed (database was reset)
    if (result.status === 'reregister_needed') {
      console.log('🔄 Database was reset, re-registering passkey...');
      return {
        status: 'reregister_needed',
        credentialId: result.credential_id
      };
    }
    
    // Store in localStorage (persists across tabs and browser restarts)
    localStorage.setItem(STORAGE_KEYS.DEVICE_FINGERPRINT, result.device_fingerprint);
    localStorage.setItem(STORAGE_KEYS.USER_ID, result.user_id);
    localStorage.setItem(STORAGE_KEYS.AUTHENTICATED, 'true');
    localStorage.setItem(STORAGE_KEYS.HAS_PASSKEY, 'true'); // Mark that device has passkey
    
    if (result.last_used_name) {
      localStorage.setItem(STORAGE_KEYS.LAST_USED_NAME, result.last_used_name);
    }
    
    emitDebugLog('🎉 Authentication complete!', 'success');
    
    return {
      success: true,
      deviceFingerprint: result.device_fingerprint,
      userId: result.user_id,
      lastUsedName: result.last_used_name
    };
    
  } catch (error) {
    emitDebugLog(`❌ Authentication failed: ${error.message}`, 'error');
    emitDebugLog(`Error type: ${error.name}`, 'error');
    if (error.code) {
      emitDebugLog(`Error code: ${error.code}`, 'error');
    }
    
    // If authentication fails with NotAllowedError, the device might not have the passkey
    // Return registration_needed status to trigger registration instead
    //
    // 2026-10-07 (Mike incident follow-up): Pass the ORIGINAL error
    // name/message through so the caller can log it in telemetry.
    // Previously the error details were discarded here (just returned
    // generic status), which meant we had no visibility into whether
    // the failure was a quick credential-not-found vs a long biometric
    // timeout vs user cancel. All three throw NotAllowedError but have
    // very different operational implications.
    //
    // 2026-10-07 Fix #4 Android REPLACEMENT — duration-gated auto-register
    // suppression. The 50-cap approach was rolled back (dormant in prod
    // at 285 credentials, no benefit). This is the lighter safety net:
    //   - QUICK NotAllowedError (<5s): OS synchronously rejected → no
    //     credential on device → genuine new user → allow auto-register
    //     (existing behavior, preserves new-user path).
    //   - SLOW NotAllowedError (>=5s): biometric prompt was shown, user
    //     interacted, something stalled or failed → NOT a new user →
    //     SUPPRESS auto-register. Caller receives an Error with
    //     `suppressedAutoRegister=true` and shows the Sign In screen so
    //     user can retry (page reload button) or get admin assistance.
    //   - EXTRA SIGNAL: localStorage still has `has_passkey` hint →
    //     user is a known returner → suppress even on quick fail. Covers
    //     the case where a returner's auth fails fast for OS reasons but
    //     cache is still intact (Fix #8 would've bridged on registration
    //     anyway, but suppressing here avoids the OS-side duplicate
    //     passkey creation that Fix #8 can't prevent).
    // Android-only — iOS has Fix #4 Fix 1 + Fix #9 handling this class
    // of issue via a different mechanism (registration-first flow).
    if (error.name === 'NotAllowedError') {
      const _authDurationMs = Math.round(
        ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - _authT0
      );
      const SLOW_FAIL_THRESHOLD_MS = 5000;
      const _isAndroid = typeof navigator !== 'undefined'
        && (navigator.userAgent || '').toLowerCase().includes('android');
      const _hasLocalPkHint = typeof localStorage !== 'undefined'
        && localStorage.getItem(STORAGE_KEYS.HAS_PASSKEY) === 'true';
      const _isSlowFail = _authDurationMs >= SLOW_FAIL_THRESHOLD_MS;
      const _shouldSuppress = _isAndroid && (_isSlowFail || _hasLocalPkHint);

      if (_shouldSuppress) {
        emitDebugLog(
          `⏸️ Suppressing Android auto-register (duration=${_authDurationMs}ms, hasLocalPkHint=${_hasLocalPkHint}) — user lands on Sign In screen`,
          'warning'
        );
        // 2026-10-11 telemetry patch: emit a SERVER-PERSISTED event
        // so we can count suppression firings from production logs.
        // Previously only `emitDebugLog` fired here, which is
        // browser-only (CustomEvent + console.log) and left zero
        // trace in the backend `debug_events` collection — the
        // suppression's effectiveness was unverifiable at scale.
        // Fire-and-forget; never let telemetry failure crash auth.
        try {
          reportClientEvent('passkey_android_suppression_fired', {
            auth_duration_ms: _authDurationMs,
            is_slow_fail: _isSlowFail,
            has_local_pk_hint: _hasLocalPkHint,
            suppress_reason: _isSlowFail ? 'slow_fail' : 'local_pk_hint',
            original_error_name: error.name,
            original_error_message: (error.message || '').slice(0, 300),
            device_fingerprint_prefix: (localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT) || '').slice(0, 12),
          });
        } catch (_e) { /* telemetry best-effort */ }
        const _w = new Error('Sign in failed. Please try again.');
        _w.originalErrorName = error.name;
        _w.originalErrorMessage = (error.message || '').slice(0, 300);
        _w.suppressedAutoRegister = true;
        _w.suppressReason = _isSlowFail ? 'slow_fail' : 'local_pk_hint';
        _w.authDurationMs = _authDurationMs;
        throw _w;
      }

      emitDebugLog('⚠️ Device does not have this passkey - will try registration instead', 'warning');
      return {
        status: 'registration_needed',
        message: 'Device passkey not found',
        originalErrorName: error.name,
        originalErrorMessage: (error.message || '').slice(0, 300),
      };
    } else if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
      const _w = new Error('Authentication timed out. Please try again and complete the biometric prompt more quickly.');
      _w.originalErrorName = error.name;
      _w.originalErrorMessage = (error.message || '').slice(0, 300);
      throw _w;
    } else if (error.name === 'NotSupportedError') {
      const _w = new Error('Passkeys are not supported on this device/browser. Please use a different device or update your browser.');
      _w.originalErrorName = error.name;
      _w.originalErrorMessage = (error.message || '').slice(0, 300);
      throw _w;
    }
    
    throw error;
  }
}

/**
 * Re-register an existing passkey after database reset
 * SIMPLIFIED: No deviceFingerprint parameter needed - backend uses credential_id
 */
export async function reregisterExistingPasskey(credentialId) {
  try {
    console.log('🔄 Re-registering existing passkey...');
    
    const response = await fetch(`${API}/auth/passkey/reregister`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credential_id: credentialId
        // device_fingerprint removed - backend uses credential_id directly
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to re-register passkey');
    }
    
    const result = await response.json();
    
    // Store in localStorage (persists across tabs and browser restarts)
    // device_fingerprint is now the credential_id from backend
    localStorage.setItem(STORAGE_KEYS.DEVICE_FINGERPRINT, result.device_fingerprint);
    localStorage.setItem(STORAGE_KEYS.USER_ID, result.user_id);
    localStorage.setItem(STORAGE_KEYS.AUTHENTICATED, 'true');
    localStorage.setItem(STORAGE_KEYS.HAS_PASSKEY, 'true'); // Mark that device has passkey
    
    console.log('✅ Passkey re-registered successfully!');
    
    return {
      success: true,
      deviceFingerprint: result.device_fingerprint,
      userId: result.user_id
    };
    
  } catch (error) {
    console.error('❌ Passkey re-registration failed:', error);
    throw error;
  }
}

/**
 * Parse the User-Agent to extract OS + browser + version signals for
 * the `passkey_flow_initiated` telemetry event. Kept intentionally
 * dependency-free and best-effort — a missing field is preferable to
 * a wrong one, so every regex is guarded and only sets its target when
 * it matches. Cost: ~5 regex tests per Sign-In tap. No PII.
 */
function parsePlatform() {
  const ua = navigator.userAgent || '';
  const uaLower = ua.toLowerCase();
  const out = {
    os: 'other',
    os_version: null,        // e.g. "17.2" (iOS) or "14" (Android)
    os_version_major: null,  // integer for easy filtering (iOS <16 unsupported passkeys)
    browser: 'other',
    browser_version: null,
    is_ios: false,
    is_android: false,
  };

  // ── OS detection ────────────────────────────────────────────────
  // iOS / iPadOS. iPads on iOS 13+ report as "Macintosh" — detect via
  // maxTouchPoints as a secondary signal. iPhone/iPod always match here.
  if (/iphone|ipad|ipod/.test(uaLower) ||
      (uaLower.includes('mac') && (navigator.maxTouchPoints || 0) > 1)) {
    out.os = 'ios';
    out.is_ios = true;
    // "OS 17_2 like Mac OS X" → 17.2
    const m = ua.match(/OS (\d+)[_.](\d+)(?:[_.](\d+))?/);
    if (m) {
      out.os_version = `${m[1]}.${m[2]}${m[3] ? '.' + m[3] : ''}`;
      out.os_version_major = parseInt(m[1], 10);
    }
  } else if (uaLower.includes('android')) {
    out.os = 'android';
    out.is_android = true;
    // "Android 14; ..." or "Android 14.0; ..."
    const m = ua.match(/Android (\d+(?:\.\d+)?)/);
    if (m) {
      out.os_version = m[1];
      out.os_version_major = parseInt(m[1].split('.')[0], 10);
    }
  } else if (uaLower.includes('windows')) {
    out.os = 'windows';
  } else if (uaLower.includes('mac')) {
    out.os = 'mac';
  } else if (uaLower.includes('linux')) {
    out.os = 'linux';
  }

  // ── Browser detection ───────────────────────────────────────────
  // Order matters: Chrome UA contains "Safari", so test Chrome first.
  // Edge contains "Chrome" AND "Edg", so test Edge first. Same for Opera.
  let m;
  if ((m = ua.match(/Edg\/(\d+(?:\.\d+)?)/))) {
    out.browser = 'edge'; out.browser_version = m[1];
  } else if ((m = ua.match(/OPR\/(\d+(?:\.\d+)?)/))) {
    out.browser = 'opera'; out.browser_version = m[1];
  } else if ((m = ua.match(/CriOS\/(\d+(?:\.\d+)?)/))) {
    // Chrome on iOS — reports differently from desktop Chrome.
    out.browser = 'chrome_ios'; out.browser_version = m[1];
  } else if ((m = ua.match(/FxiOS\/(\d+(?:\.\d+)?)/))) {
    out.browser = 'firefox_ios'; out.browser_version = m[1];
  } else if ((m = ua.match(/Chrome\/(\d+(?:\.\d+)?)/))) {
    out.browser = 'chrome'; out.browser_version = m[1];
  } else if ((m = ua.match(/Firefox\/(\d+(?:\.\d+)?)/))) {
    out.browser = 'firefox'; out.browser_version = m[1];
  } else if ((m = ua.match(/Version\/(\d+(?:\.\d+)?).*Safari/))) {
    out.browser = 'safari'; out.browser_version = m[1];
  }

  // ── In-app browser detection ────────────────────────────────────
  // OPTION A FIX 2026-08-29: use the shared, comprehensive
  // detector from browserDetection.js instead of the previous
  // 4-app regex. Aligns detection between `client_session_start`
  // (App.js block screen) and every downstream `passkey_flow_*`
  // event. Previously `passkey_flow_initiated.is_in_app_browser`
  // silently missed Snapchat/TikTok/LinkedIn/Reddit/Discord/etc.
  // — meaning users who dismissed the block from those apps
  // showed up in the flow event as `is_in_app_browser=false`,
  // breaking correlation.
  //
  // Note: browserDetection.js is still UA-based. Chrome Custom
  // Tabs on Android (used by Google App QR scanner) is NOT caught
  // — no reliable UA signature exists. If Google App emerges as
  // an offender in the logs, we may need to build a heuristic
  // (e.g., detect via referrer, timing, or a first-load probe).
  const _inApp = detectInAppBrowser();
  out.is_in_app_browser = _inApp.isInApp;
  out.in_app_name = _inApp.appName;  // e.g. 'Facebook', 'Google App', 'Android WebView'

  return out;
}

/**
 * Fire the `passkey_flow_initiated` telemetry event. Called once at the
 * top of `authenticateUser()` — every "Tap Here to Sign In" button press
 * reloads the page, which re-runs `authenticateUser()`, so this event
 * captures every user-initiated auth attempt.
 *
 * PURPOSE: Diagnose the 1-2 users/night who tap Sign In but never see a
 * biometric prompt. We need to see whether they:
 *   1. Have `is_pk_supported = false` (browser doesn't support WebAuthn)
 *   2. Are on an outdated iOS (`os_version_major < 16` — no passkeys)
 *   3. Are inside an in-app browser (Facebook/Instagram/etc.)
 *   4. Land on a specific branch of `authenticateUser()` that silently
 *      falls through without prompting
 *
 * Fire-and-forget: never throws, never awaits, never blocks the auth
 * flow. If the telemetry POST fails, we just move on.
 */
function emitPasskeyFlowInitiated(flowChosen, extras = {}) {
  try {
    const platform = parsePlatform();
    const payload = {
      is_pk_supported: isPasskeySupported(),
      flow_chosen: flowChosen,
      has_local_pk_hint: localStorage.getItem(STORAGE_KEYS.HAS_PASSKEY) === 'true',
      has_cached_auth: !!(
        localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT) &&
        localStorage.getItem(STORAGE_KEYS.AUTHENTICATED) === 'true'
      ),
      using_fallback: localStorage.getItem(STORAGE_KEYS.USING_FALLBACK) === 'true',
      // Device fingerprint prefix 2026-10-07 — first 12 chars of the
      // cached device_fingerprint, if present. Collapses the 3-hop
      // identification trail (client_session_id → register_succeeded →
      // queue_join dfp_prefix) into a 1-hop lookup when diagnosing
      // Mike-type passkey failures. Empty string when fingerprint is
      // absent (fresh browser profile / cleared localStorage — the
      // exact Fix #52 scenario). Purely diagnostic, additive to the
      // payload. Never used for auth decisions.
      device_fingerprint_prefix: (localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT) || '').slice(0, 12),
      // Platform signals (parsePlatform never throws)
      os: platform.os,
      os_version: platform.os_version,
      os_version_major: platform.os_version_major,
      browser: platform.browser,
      browser_version: platform.browser_version,
      is_in_app_browser: platform.is_in_app_browser,
      in_app_name: platform.in_app_name,
      // Truncated UA for correlation (server also truncates to 500 chars).
      ua: (navigator.userAgent || '').slice(0, 300),
      ...extras,
    };

    // Fire-and-forget POST — no await, no error propagation. If the
    // backend is unreachable we silently skip; the goal is diagnostics,
    // not blocking auth on a telemetry write.
    //
    // Routes through reportClientEvent() so client_session_id (Fix #11)
    // auto-attaches for correlation.
    reportClientEvent('passkey_flow_initiated', payload);

    emitDebugLog(
      `📡 passkey_flow_initiated: flow=${flowChosen}, os=${platform.os}${platform.os_version ? ' ' + platform.os_version : ''}, browser=${platform.browser}, pk_supported=${payload.is_pk_supported}`,
      'info'
    );
  } catch (e) {
    // Absolutely never let telemetry crash the auth flow.
    console.warn('emitPasskeyFlowInitiated failed:', e);
  }
}

/**
 * Main authentication flow - REGISTRATION-FIRST approach
 * 
 * This approach tries to register a passkey first.
 * - If device has no passkey: registration proceeds smoothly (no confusing modal!)
 * - If device already has passkey: WebAuthn detects it via excludeCredentials and throws InvalidStateError
 * - On InvalidStateError: fall back to authentication
 */
export async function authenticateUser() {
  // ── Telemetry: fire ONCE per authenticateUser() invocation. This
  // corresponds 1:1 with a user tapping "Tap Here to Sign In" (which
  // reloads the page and re-triggers this useEffect chain). We compute
  // `flow_chosen` from the same signals the flow-selection logic below
  // uses, so a silent failure in any branch can be attributed to the
  // exact code path the user took. Fire-and-forget — never blocks auth.
  //
  // Flow codes:
  //   cache_hit_passkey       — cached auth, non-fallback user (fast path)
  //   cache_hit_fallback      — cached auth, fallback user (needs backend validation)
  //   whitelist_fallback      — emergent.sh / preview host, auto-fallback
  //   no_webauthn_fallback    — browser lacks WebAuthn API, auto-fallback
  //   android_auth_first      — Android device, authentication-first flow
  //   ios_direct_auth         — iOS/Desktop with HAS_PASSKEY hint, direct auth
  //   ios_register_first      — iOS/Desktop new device, registration-first
  //
  // Deferred to inside the function so we can pick the right code AFTER
  // the same guards the branching logic uses (support check, whitelist,
  // cache).
  const _flowInit = () => {
    const cachedFingerprint = localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT);
    const cachedAuthenticated = localStorage.getItem(STORAGE_KEYS.AUTHENTICATED);
    const usingFallback = localStorage.getItem(STORAGE_KEYS.USING_FALLBACK) === 'true';
    const hostname = window.location.hostname;
    const isEmergentHost = hostname.includes('emergentagent.com') || hostname.includes('emergent.sh');
    const isAndroid = navigator.userAgent.toLowerCase().includes('android');
    const hasPasskeyHint = localStorage.getItem(STORAGE_KEYS.HAS_PASSKEY) === 'true';

    let flow;
    if (cachedFingerprint && cachedAuthenticated === 'true') {
      flow = usingFallback ? 'cache_hit_fallback' : 'cache_hit_passkey';
    } else if (isEmergentHost) {
      flow = 'whitelist_fallback';
    } else if (!isPasskeySupported()) {
      flow = 'no_webauthn_fallback';
    } else if (isAndroid) {
      flow = 'android_auth_first';
    } else if (hasPasskeyHint) {
      flow = 'ios_direct_auth';
    } else {
      flow = 'ios_register_first';
    }
    emitPasskeyFlowInitiated(flow);
  };
  _flowInit();

  // 2026-10-11 telemetry patch: Snapshot pre-register localStorage
  // state at authenticateUser() entry, BEFORE any OS call or
  // registerPasskey() write. These values are propagated into the
  // Android `android_reg_empty_exclude_dispatched/_succeeded` events
  // so the `_succeeded` event's identity signals reflect the
  // state BEFORE registration completed, not after. Prior behavior
  // read localStorage at emission time, which made `_succeeded`
  // events show `has_local_pk_hint=True` + `has_cached_auth=True`
  // on EVERY successful register (artifact of registerPasskey()'s
  // own localStorage writes at `passkeyUtils.js:401-404`),
  // masking Mike-class returners vs legitimately new users. See
  // the Verified-Facts entry on telemetry artifacts in
  // CRITICAL_AGENT_NOTES.md.
  const _preRegHasLocalPkHint = localStorage.getItem(STORAGE_KEYS.HAS_PASSKEY) === 'true';
  const _preRegHasCachedAuth = !!(
    localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT) &&
    localStorage.getItem(STORAGE_KEYS.AUTHENTICATED) === 'true'
  );
  const _preRegDeviceFingerprintPrefix = (localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT) || '').slice(0, 12);
  const _preRegUserIdPrefix = (localStorage.getItem(STORAGE_KEYS.USER_ID) || '').slice(0, 12);

  // Check if already authenticated (now persists across tabs and browser restarts)
  const cachedFingerprint = localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT);
  const cachedAuthenticated = localStorage.getItem(STORAGE_KEYS.AUTHENTICATED);
  
  if (cachedFingerprint && cachedAuthenticated === 'true') {
    // For FALLBACK users: validate with backend before trusting cache
    // This handles orphaned credentials after session reset
    const usingFallback = localStorage.getItem(STORAGE_KEYS.USING_FALLBACK) === 'true';
    
    if (usingFallback) {
      console.log('🔍 Fallback user detected - validating with backend...');

      // Cached snapshot for fail-open return paths — mirrors the B1
      // passkey-side pattern (`trustCacheAndProceed`) so a transient
      // network hiccup on refresh doesn't kick the user to re-auth.
      // Only clear when the backend cleanly says `exists=false`. Rationale
      // + telemetry filters documented in DEFERRED_FIXES.md > B1 entry.
      const trustFallbackCacheAndProceed = (reasonMessage) => {
        console.log(`✅ Fallback validation ${reasonMessage} - trusting cache (fail-open)`);
        return {
          success: true,
          deviceFingerprint: cachedFingerprint,
          userId: localStorage.getItem(STORAGE_KEYS.USER_ID),
          lastUsedName: localStorage.getItem(STORAGE_KEYS.LAST_USED_NAME),
          fromCache: true,
          usingFallback: true
        };
      };

      try {
        const response = await fetch(`${API}/auth/validate-fallback/${encodeURIComponent(cachedFingerprint)}`);
        if (response.ok) {
          const data = await response.json();
          if (!data.exists) {
            // Backend cleanly says the fallback user does not exist —
            // this is the ONLY fail-closed branch.
            console.log('⚠️ Fallback user not found in database - clearing cache and re-authenticating');
            emitDebugLog(
              '🔄 [validate_fallback_ghost_cache] Fallback credentials expired - re-authenticating...',
              'info'
            );
            clearAllAuthData();
            // Continue to passkey flow below (don't return here)
          } else {
            // Fallback user still valid
            console.log('✅ Fallback user validated');
            return {
              success: true,
              deviceFingerprint: cachedFingerprint,
              userId: localStorage.getItem(STORAGE_KEYS.USER_ID),
              lastUsedName: localStorage.getItem(STORAGE_KEYS.LAST_USED_NAME),
              fromCache: true,
              usingFallback: true
            };
          }
        } else {
          // Backend responded with non-OK — transient, trust cache
          // (fail-open). Distinct tag so 5xx spikes are spottable in
          // the debug panel.
          emitDebugLog(
            `⚠️ [validate_fallback_backend_error] Fallback validation returned HTTP ${response.status} - trusting cache`,
            'warning'
          );
          return trustFallbackCacheAndProceed(`backend HTTP ${response.status}`);
        }
      } catch (error) {
        // Fetch threw (network unreachable, DNS, TLS, timeout, Edge/Windows
        // network-stack hiccup on refresh). Trust cache (fail-open),
        // include error name in telemetry so transport-failure types
        // can be distinguished.
        const errName = (error && error.name) || 'UnknownError';
        const errMsg = (error && error.message) || 'unknown';
        emitDebugLog(
          `⚠️ [validate_fallback_network_error] Fallback validation network failure (${errName}: ${errMsg}) - trusting cache`,
          'warning'
        );
        return trustFallbackCacheAndProceed(`network error (${errName})`);
      }
    } else {
      // Passkey user - validate cached fingerprint against backend
      // before trusting (B1 fix, 2026-09-21).
      //
      // Rationale: prior to B1, this branch trusted the cache
      // unconditionally. If the server-side user doc had been
      // deleted (e.g. inactive-passkey-cleanup at 6am session reset
      // wipes ghost accounts that registered but never sang), the
      // client would remain "signed in" from localStorage but the
      // server would silently insert a brand-new user doc on the
      // next queue-join — severing identity continuity (the "ghost
      // signed-in" state observed with user XD / George Faris on
      // 2026-09-21 Sunday night session).
      //
      // Posture: FAIL-OPEN on network/backend errors 2026-09-21.
      // Only wipe cache when the backend cleanly says exists=false
      // (the real ghost-cache signal). On any transport failure
      // (fetch throw) or non-2xx response, trust the cache and
      // proceed — Fix #8 is anti-gaming friction not security, so
      // sacrificing the tighter guarantee here to eliminate the
      // "network hiccup kicks user to sign-in" annoyance is the
      // right tradeoff. See DEFERRED_FIXES.md "Recently shipped —
      // under observation" > B1 entry for the full analysis and
      // rollback lever.
      //
      // Telemetry: every non-happy-path outcome fires a distinct
      // emitDebugLog so admin can see exactly which branch fired
      // in the debug panel:
      //   - exists=false          → clearing cache, re-auth
      //   - backend 5xx / non-ok  → trusting cache anyway (fail-open)
      //   - fetch threw           → trusting cache anyway (fail-open)
      console.log('🔍 Passkey user detected - validating with backend...');

      // Cached snapshot for the fail-open return paths so the fetch's
      // in-flight state doesn't shift out from under us.
      const trustCacheAndProceed = (reasonMessage) => {
        console.log(`✅ Passkey validation ${reasonMessage} - trusting cache (fail-open)`);
        return {
          success: true,
          deviceFingerprint: cachedFingerprint,
          userId: localStorage.getItem(STORAGE_KEYS.USER_ID),
          lastUsedName: localStorage.getItem(STORAGE_KEYS.LAST_USED_NAME),
          fromCache: true,
          usingFallback: false
        };
      };

      try {
        const response = await fetch(`${API}/auth/validate-user/${encodeURIComponent(cachedFingerprint)}`);
        if (response.ok) {
          const data = await response.json();
          if (data.exists) {
            // Passkey user record still valid on server
            console.log('✅ Passkey user validated');
            return {
              success: true,
              deviceFingerprint: cachedFingerprint,
              userId: localStorage.getItem(STORAGE_KEYS.USER_ID),
              lastUsedName: localStorage.getItem(STORAGE_KEYS.LAST_USED_NAME),
              fromCache: true,
              usingFallback: false
            };
          }
          // Server has no record → ghost-signed-in state → wipe and re-auth.
          // This is the ONLY fail-closed branch — a clean, authoritative
          // "your user doc doesn't exist" from the server.
          console.log('⚠️ Passkey user not found in database - clearing cache and re-authenticating');
          emitDebugLog(
            '🔄 [validate_user_ghost_cache] Server-side user record missing - re-authenticating',
            'info'
          );
          clearAllAuthData();
          // Fall through to normal passkey flow below (don't return here)
        } else {
          // Backend responded with a non-OK status — treat as transient,
          // trust cache (fail-open). Emit a distinct telemetry tag so we
          // can spot repeated 5xx patterns in the debug panel.
          emitDebugLog(
            `⚠️ [validate_user_backend_error] Passkey validation returned HTTP ${response.status} - trusting cache`,
            'warning'
          );
          return trustCacheAndProceed(`backend HTTP ${response.status}`);
        }
      } catch (error) {
        // Fetch threw (network unreachable, DNS, TLS, timeout).
        // Trust cache (fail-open) and emit a distinct telemetry tag
        // including the error name so we can distinguish transport
        // failure types in the debug panel.
        const errName = (error && error.name) || 'UnknownError';
        const errMsg = (error && error.message) || 'unknown';
        emitDebugLog(
          `⚠️ [validate_user_network_error] Passkey validation network failure (${errName}: ${errMsg}) - trusting cache`,
          'warning'
        );
        return trustCacheAndProceed(`network error (${errName})`);
      }
    }
  }
  
  // WHITELIST CHECK: Automatically use fallback for Emergent platform preview
  const hostname = window.location.hostname;
  if (hostname.includes('emergentagent.com') || hostname.includes('emergent.sh')) {
    emitDebugLog('⚠️ Emergent platform detected, using fallback mode', 'warning');
    return enableFallbackMode();
  }
  
  // Check if passkeys are supported
  const supported = isPasskeySupported();
  
  if (!supported) {
    // FALLBACK MODE: Browser doesn't support WebAuthn at all - this is a clear case
    emitDebugLog('⚠️ Passkeys not supported by browser, using fallback mode', 'warning');
    return enableFallbackMode();
  }
  
  // NOTE: We no longer auto-fallback based on isPlatformAuthenticatorAvailable()
  // This check is unreliable and can return false positives/negatives
  // Let the actual passkey operation determine if it works
  
  // Detect Android for flow selection
  const isAndroid = navigator.userAgent.toLowerCase().includes('android');
  
  // Check if device has previously registered a passkey (localStorage hint)
  const hasPasskey = localStorage.getItem(STORAGE_KEYS.HAS_PASSKEY);
  
  // ANDROID: Use AUTHENTICATION-FIRST flow
  // This prevents duplicate passkeys since auth checks the device's passkey store directly
  // If passkey exists on device (even in private mode), auth will find it
  if (isAndroid) {
    emitDebugLog('📱 Android detected - using authentication-first flow (prevents duplicates)', 'info');
    
    // Try authentication first
    try {
      emitDebugLog('🔐 Attempting passkey authentication...', 'info');
      const authResult = await authenticateWithPasskey();
      
      // Check if re-registration is needed (database was reset)
      if (authResult.status === 'reregister_needed') {
        emitDebugLog('🔄 Re-linking passkey after database reset...', 'info');
        const reregResult = await reregisterExistingPasskey(authResult.credentialId);
        return reregResult;
      }
      
      // Check if registration is needed (no passkey on device)
      if (authResult.status === 'registration_needed') {
        emitDebugLog('📝 No passkey on device - proceeding to registration...', 'info');
        try {
          const userId = 'user_' + Date.now();
          // ROLLBACK 2026-10-07: Fix #4 Android (exclude-list approach)
          // was shipped this morning but found to be a no-op in production
          // (285 credentials > 50-cap → helper always returned []). Rolled
          // back and replaced by the duration-gated suppression in
          // authenticateWithPasskey()'s NotAllowedError catch. See
          // DEFERRED_FIXES.md "Fix #4 Android — ROLLED BACK + REPLACED".
          try {
            emitPasskeyFlowInitiated('android_reg_empty_exclude_dispatched', {
              trigger: 'from_status_registration_needed',
              // 2026-10-11 telemetry patch — propagate pre-register snapshot
              pre_register_has_local_pk_hint: _preRegHasLocalPkHint,
              pre_register_has_cached_auth: _preRegHasCachedAuth,
              pre_register_device_fingerprint_prefix: _preRegDeviceFingerprintPrefix,
              pre_register_user_id_prefix: _preRegUserIdPrefix,
            });
          } catch (_e) { /* telemetry best-effort */ }
          const regResult = await registerPasskey(userId, 'Karaoke User', []);
          try {
            emitPasskeyFlowInitiated('android_reg_empty_exclude_succeeded', {
              trigger: 'from_status_registration_needed',
              new_user_id_prefix: userId.slice(0, 12),
              // 2026-10-11 telemetry patch — pre-register snapshot
              // (reliable Mike-vs-new-user signal; localStorage was
              // overwritten by registerPasskey() so emission-time
              // values would be useless)
              pre_register_has_local_pk_hint: _preRegHasLocalPkHint,
              pre_register_has_cached_auth: _preRegHasCachedAuth,
              pre_register_device_fingerprint_prefix: _preRegDeviceFingerprintPrefix,
              pre_register_user_id_prefix: _preRegUserIdPrefix,
            });
          } catch (_e) { /* telemetry best-effort */ }
          emitDebugLog('✅ New passkey registered successfully', 'success');
          return regResult;
        } catch (regError) {
          emitDebugLog(`❌ Registration failed: ${regError.name} - ${regError.message}`, 'error');
          throw regError;
        }
      }
      
      // Success - existing passkey used!
      emitDebugLog('✅ Authentication successful (existing passkey)', 'success');
      return authResult;
      
    } catch (authError) {
      emitDebugLog(`⚠️ Authentication failed: ${authError.name} - ${authError.message}`, 'warning');
      
      // 2026-10-07: suppress-aware check. The duration-gated suppression in
      // authenticateWithPasskey() may re-throw a wrapped Error flagged
      // with `suppressedAutoRegister=true`. When present, DO NOT fall
      // back to registration — propagate the error up so the caller
      // lands the user on the Sign In screen (page-reload retry path).
      // This is the Mike-incident safety net (replaces Fix #4 Android).
      if (authError.suppressedAutoRegister) {
        emitDebugLog(
          `⏸️ Android auto-register suppressed (${authError.suppressReason}, ${authError.authDurationMs}ms) — propagating to Sign In screen`,
          'warning'
        );
        throw authError;
      }

      // If no passkey exists, authentication will fail - proceed to registration
      // Common errors indicating no passkey: NotAllowedError (user cancelled or no credential)
      if (authError.name === 'NotAllowedError' || authError.name === 'SecurityError') {
        emitDebugLog('📝 No existing passkey found - proceeding to registration...', 'info');
        
        try {
          const userId = 'user_' + Date.now();
          // ROLLBACK 2026-10-07: see note at the sibling branch above.
          // Reverted to `existingCredentialIds=[]`. The Mike-type bug
          // this used to try to prevent is now addressed by the
          // duration-gated suppression in authenticateWithPasskey() —
          // slow NotAllowedErrors throw `suppressedAutoRegister=true`
          // and never reach this branch, so this branch is now reserved
          // for genuine new users (fast NotAllowedError, no local hint).
          try {
            emitPasskeyFlowInitiated('android_reg_empty_exclude_dispatched', {
              trigger: 'from_auth_error',
              auth_error_name: authError.name,
              // 2026-10-11 telemetry patch — propagate pre-register snapshot
              pre_register_has_local_pk_hint: _preRegHasLocalPkHint,
              pre_register_has_cached_auth: _preRegHasCachedAuth,
              pre_register_device_fingerprint_prefix: _preRegDeviceFingerprintPrefix,
              pre_register_user_id_prefix: _preRegUserIdPrefix,
            });
          } catch (_e) { /* telemetry best-effort */ }
          const regResult = await registerPasskey(userId, 'Karaoke User', []);
          try {
            emitPasskeyFlowInitiated('android_reg_empty_exclude_succeeded', {
              trigger: 'from_auth_error',
              auth_error_name: authError.name,
              new_user_id_prefix: userId.slice(0, 12),
              // 2026-10-11 telemetry patch — pre-register snapshot
              pre_register_has_local_pk_hint: _preRegHasLocalPkHint,
              pre_register_has_cached_auth: _preRegHasCachedAuth,
              pre_register_device_fingerprint_prefix: _preRegDeviceFingerprintPrefix,
              pre_register_user_id_prefix: _preRegUserIdPrefix,
            });
          } catch (_e) { /* telemetry best-effort */ }
          emitDebugLog('✅ New passkey registered successfully', 'success');
          return regResult;
        } catch (regError) {
          emitDebugLog(`❌ Registration failed: ${regError.name} - ${regError.message}`, 'error');
          throw regError;
        }
      }
      
      // Other errors - throw to trigger retry/fallback flow
      throw authError;
    }
  }
  
  // iOS/DESKTOP: Use REGISTRATION-FIRST flow with excludeCredentials
  // This avoids the confusing "Sign In" modal on iOS
  
  if (hasPasskey === 'true') {
    // Device has passkey hint - skip registration attempt, go straight to authentication
    emitDebugLog('🔑 Device has existing passkey - authenticating directly...', 'info');
    
    try {
      const authResult = await authenticateWithPasskey();
      
      // Check if re-registration is needed (database was reset)
      if (authResult.status === 'reregister_needed') {
        emitDebugLog('🔄 Re-linking passkey after database reset...', 'info');
        const reregResult = await reregisterExistingPasskey(authResult.credentialId);
        return reregResult;
      }
      
      // Success - returning user authenticated!
      emitDebugLog('✅ Authentication successful', 'success');
      return authResult;
      
    } catch (error) {
      emitDebugLog(`❌ Authentication failed: ${error.message}`, 'error');
      // Clear the HAS_PASSKEY flag and try registration (passkey might have been deleted)
      localStorage.removeItem(STORAGE_KEYS.HAS_PASSKEY);
      emitDebugLog('🔄 Falling back to registration flow...', 'warning');
      // Continue to registration attempt below
    }
  }
  
  // REGISTRATION-FIRST APPROACH (for iOS/Desktop new devices or auth failure):
  // Try to register a passkey with excludeCredentials containing ALL existing passkeys
  // - New device: registration succeeds (no modal!)
  // - Returning device: WebAuthn throws InvalidStateError (credential already exists)
  // - On InvalidStateError: fall back to authentication
  
  emitDebugLog('🎯 Starting registration-first flow (iOS/Desktop)...', 'info');
  
  // Fetch all existing credential IDs from backend for excludeCredentials.
  //
  // FIX #4 (iOS-only, 2026-08-29). Fail LOUD on any list-credentials
  // fetch failure. Silently proceeding with an empty excludeCredentials
  // list is how ~1000 duplicate credentials accumulated in the prod DB
  // over the past several months on flaky bar Wi-Fi:
  //   1. User taps Sign In on spotty Wi-Fi
  //   2. list-credentials fetch fails silently (old catch swallowed it)
  //   3. registerPasskey() called with existingCredentialIds = []
  //   4. OS asked "collision with []?" → no → creates fresh passkey
  //   5. User "signs in" but with a brand-new identity → previous
  //      turn count, name, group affiliation all lost
  //
  // Fix: throw on ANY fetch failure (network OR non-ok status). User
  // sees "please try again", retries when the network recovers, and
  // the retry uses a proper exclusion list. If the collision then
  // fires (existing passkey on device), Fix #9's login-fallback path
  // covers the recovery.
  //
  // Emits telemetry so we can measure how often this gate fires and
  // spot a bar-Wi-Fi problem masquerading as a fix bug.
  //
  // Scope: iOS/Desktop registration path only. Android's version of
  // this bug is deferred (host decision 2026-08-29) — it uses empty
  // excludeCredentials by design and the fix requires a larger
  // redesign. Rename+purge will clear Android's accumulated damage
  // anyway.
  let existingCredentialIds = [];
  try {
    const credsResponse = await fetch(`${API}/auth/passkey/list-credentials`, {
      method: 'GET'
    });
    if (!credsResponse.ok) {
      // Catches 500/502/503 which the OLD code silently swallowed
      // because `credsResponse.ok` was false but no exception thrown.
      throw new Error(`Backend returned HTTP ${credsResponse.status}`);
    }
    const credsData = await credsResponse.json();
    existingCredentialIds = credsData.credential_ids || [];
    emitDebugLog(`📋 Found ${existingCredentialIds.length} existing credentials in database`, 'info');
  } catch (error) {
    emitDebugLog(
      `❌ Fix #4: aborting registration to prevent duplicate — list-credentials fetch failed (${error.message})`,
      'error'
    );
    // Direct-measure telemetry: every firing = one prevented duplicate.
    // Rides the existing `passkey_flow_initiated` whitelist so no
    // backend change needed. See FIX9_POST_SESSION_LOG_WATCHLIST.md
    // section 6b for the queries to interpret this.
    emitPasskeyFlowInitiated('ios_register_gated_on_fetch_failure', {
      fetch_error_name: error.name,
      fetch_error_message: (error.message || '').slice(0, 200),
      backend_url: API,
    });
    // Surface user-actionable message. Propagates through the caller's
    // catch in App.js which increments fail_count and shows the user
    // their normal error UI.
    throw new Error(
      'Network problem checking sign-in — please try again in a moment.'
    );
  }
  
  // Try registration with excludeCredentials
  try {
    const userId = 'user_' + Date.now();
    
    emitDebugLog('📝 Attempting passkey registration...', 'info');
    const regResult = await registerPasskey(userId, 'Karaoke User', existingCredentialIds);
    
    // Registration succeeded - this is a new device!
    emitDebugLog('✅ New device registered successfully', 'success');
    return regResult;
    
  } catch (error) {
    emitDebugLog(`⚠️ Registration attempt failed: ${error.name} - ${error.message}`, 'warning');
    
    // CASE 1: Device already has passkey - fall back to authentication
    // iOS/Safari: throws InvalidStateError (the "fast refusal" variant)
    //
    // TELEMETRY PARITY 2026-08-29: emits the same
    // `passkey_flow_initiated` event that Fix #9 uses so we can
    // measure how often iOS chooses THIS path vs. the slow-refusal
    // NotAllowedError path (Fix #9 territory). Together the two
    // counts tell us the iOS error-variance distribution under the
    // current 1000+ credential bloat, and give us before/after data
    // for the domain rename.
    if (error.name === 'InvalidStateError' || error.name === 'NotReadableError') {
      emitDebugLog('🔄 Device already has passkey - falling back to authentication...', 'info');
      emitPasskeyFlowInitiated('ios_invalidstate_login_fallback', {
        trigger_error_name: error.name,
        trigger_error_message: (error.message || '').slice(0, 200),
        existing_credentials_count: existingCredentialIds.length,
      });
      
      try {
        const authResult = await authenticateWithPasskey();
        
        // Check if re-registration is needed (database was reset)
        if (authResult.status === 'reregister_needed') {
          emitDebugLog('🔄 Re-linking passkey after database reset...', 'info');
          const reregResult = await reregisterExistingPasskey(authResult.credentialId);
          return reregResult;
        }
        
        // Success - returning user authenticated!
        emitDebugLog('✅ Authentication successful', 'success');
        return authResult;
        
      } catch (authError) {
        emitDebugLog(`❌ Authentication failed: ${authError.message}`, 'error');
        throw authError;
      }
    }
    
    // ─────────────────────────────────────────────────────────────
    // FIX #9 — iCloud passkey collision recovery (Kady 2026-08-27).
    //
    // On iOS/Safari, when a user's localStorage has been cleared but
    // their iCloud Keychain still holds a passkey for this rp_id, the
    // registration ceremony we just fired should have thrown
    // InvalidStateError (handled above). Instead, iOS conflates
    // InvalidStateError and NotAllowedError for privacy reasons and
    // surfaces it as a generic "Registration not allowed" error.
    // Without this recovery leg, the user is stuck — the OS refused
    // to duplicate their credential (correct behavior) but our
    // frontend has no path forward.
    //
    // GUARD FIX 2026-08-29: registerPasskey() at line 387 rewraps
    // native NotAllowedError as a generic `new Error('Registration
    // not allowed...')` — so by the time the error reaches this
    // catch, error.name is 'Error', not 'NotAllowedError'. The
    // original guard `error.name === 'NotAllowedError'` never
    // matched in production. We match on BOTH the native name AND
    // the wrapped message string so the guard is resilient
    // regardless of whether the wrapping stays or is later removed.
    // Discovered via a real user test 2026-08-29 21:41 UTC where
    // Fix #9 failed to fire on the exact signature it was designed
    // to catch.
    //
    // Guard against false positives (genuine user cancellations, no
    // existing credential to fall back to):
    //
    //   • existingCredentialIds.length > 0
    //     — There MUST be at least one credential in the DB for the
    //       fallback login ceremony to have anything to match against.
    //       If the credential list is empty, NotAllowedError almost
    //       certainly means user-cancel, not iCloud collision.
    //
    //   • The Kady signature specifically hits here because:
    //     - She's on iOS/Safari (only path that reaches this catch —
    //       Android uses auth-first, handled up above)
    //     - Her HAS_PASSKEY hint is missing (localStorage cleared)
    //     - Her credential IS in the DB (someone else registered it,
    //       or it survived a DB restore — either way, non-empty)
    //
    // Worst-case false positive: a genuinely-new user cancels the
    // Face ID prompt during registration. We'd re-prompt as Login,
    // they'd cancel again, and see "Passkey operation failed". Two
    // prompts instead of one — mildly worse UX, but not a hard
    // failure. Telemetry (emitPasskeyFlowInitiated with the fix9
    // marker) lets us measure the false-positive rate in the field.
    //
    // Cannot infinite-loop: authenticateWithPasskey() either returns
    // a result or throws once. If it throws, we rethrow. Done.
    // ─────────────────────────────────────────────────────────────
    const isNotAllowedCollision =
      error.name === 'NotAllowedError' ||
      (error.message && error.message.includes('Registration not allowed'));

    if (isNotAllowedCollision && existingCredentialIds.length > 0) {
      emitDebugLog(
        `🩹 Fix #9: NotAllowedError during registration with ${existingCredentialIds.length} existing credentials — attempting login fallback (likely iCloud passkey collision)`,
        'warning'
      );
      // Emit dedicated telemetry so we can measure how often this
      // recovery leg fires and its success rate. Reuses the existing
      // whitelisted `passkey_flow_initiated` event with a distinct
      // flow_chosen value.
      emitPasskeyFlowInitiated('ios_fix9_login_fallback', {
        trigger_error_name: error.name,
        trigger_error_message: (error.message || '').slice(0, 200),
        existing_credentials_count: existingCredentialIds.length,
      });

      try {
        const authResult = await authenticateWithPasskey();

        // If DB was reset, treat the same as the InvalidStateError path
        if (authResult.status === 'reregister_needed') {
          emitDebugLog('🔄 Re-linking passkey after database reset (via Fix #9 path)...', 'info');
          const reregResult = await reregisterExistingPasskey(authResult.credentialId);
          return reregResult;
        }

        // Login worked → Kady's scenario confirmed → user is in.
        emitDebugLog('✅ Fix #9 recovery successful — login found the iCloud passkey', 'success');
        return authResult;

      } catch (authError) {
        // Login also failed. Three likely reasons:
        //   1. Genuine user cancel on the second prompt (false positive)
        //   2. User has NO existing credential in the DB — Fix #9 misfired
        //      because the guard only checks `existingCredentialIds.length > 0`
        //      (which is always true with the bloated DB), not whether ANY
        //      of those credentials belong to THIS device. This is
        //      Danae's scenario 2026-08-30.
        //   3. Genuine registration NotAllowedError that wasn't a
        //      collision (e.g., authenticator hardware issue)
        // Either way, surface the ORIGINAL registration error so the
        // user's stored fail_count reflects the actual failure they
        // hit, not our recovery attempt.
        emitDebugLog(
          `❌ Fix #9 fallback also failed (${authError.name}: ${authError.message}) — surfacing original registration error`,
          'error'
        );
        // Direct-measure telemetry: closes the visibility gap where we
        // couldn't tell if Fix #9 rescued users or silently failed.
        // Ship 2026-08-30 in response to Danae incident. Rides the
        // whitelisted `passkey_flow_initiated` event.
        emitPasskeyFlowInitiated('ios_fix9_fallback_login_failed', {
          register_error_name: error.name,
          register_error_message: (error.message || '').slice(0, 200),
          login_error_name: authError.name,
          login_error_message: (authError.message || '').slice(0, 200),
          existing_credentials_count: existingCredentialIds.length,
        });
        throw error;
      }
    }

    // NO AUTOMATIC FALLBACK FOR AMBIGUOUS ERRORS
    // User can retry by refreshing the page
    // After 3 consecutive failures, they'll see option to switch to classic mode
    emitDebugLog(`❌ Passkey operation failed - please try again`, 'error');
    throw error;
  }
}

/**
 * Get current device fingerprint from storage
 */
export function getCurrentDeviceFingerprint() {
  return localStorage.getItem(STORAGE_KEYS.DEVICE_FINGERPRINT);
}

/**
 * Get current user ID from storage
 */
export function getUserId() {
  return localStorage.getItem(STORAGE_KEYS.USER_ID);
}

/**
 * Get last used name from storage
 */
export function getLastUsedName() {
  return localStorage.getItem(STORAGE_KEYS.LAST_USED_NAME);
}

/**
 * Clear storage (for testing/debugging)
 */
export function clearSession() {
  localStorage.removeItem(STORAGE_KEYS.DEVICE_FINGERPRINT);
  localStorage.removeItem(STORAGE_KEYS.USER_ID);
  localStorage.removeItem(STORAGE_KEYS.AUTHENTICATED);
  localStorage.removeItem(STORAGE_KEYS.LAST_USED_NAME);
  localStorage.removeItem(STORAGE_KEYS.HAS_PASSKEY);
  console.log('🗑️ Storage cleared');
}
